import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lock, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
    } catch (err) {
      toast.error(err.message || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden" data-testid="login-page">
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="login-bg-video"
        >
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-[400px] mx-4 animate-fade-in">
        {/* Brand */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold tracking-tight heading-tight text-white" data-testid="brand-title">
            TRANSFORM
          </h1>
          <p className="text-sm mt-2" style={{ color: 'rgba(235, 235, 245, 0.6)' }}>
            Team Hub
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-black/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="tag-label text-white/60 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>
                EMAIL
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@transform.ch"
                  className="pl-10 bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 h-11 rounded-xl focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF]"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="tag-label text-white/60 block" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>
                PASSWORD
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 bg-[#2C2C2E] border-white/10 text-white placeholder:text-white/30 h-11 rounded-xl focus:ring-1 focus:ring-[#0A84FF] focus:border-[#0A84FF]"
                  required
                />
              </div>
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-[#0A84FF] hover:opacity-85 text-white font-semibold rounded-xl transition-opacity duration-150"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
              {!isLoading && <ArrowRight className="w-4 h-4 ml-1" />}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(235, 235, 245, 0.3)' }}>
          Swiss Precision. Elite Results.
        </p>
      </div>
    </div>
  );
}
